import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './styles.module.scss';
import classes2 from '../files/styles.module.scss';
import { createCn } from '../../helpers/utils';
import {
  aiSettingsService,
  AiProviderKind,
  AiSettings,
  AiTestResult,
} from '../../services/ai-settings.service';
import AiSettingsGroup from './components/ai-settings-group.component';
import DiarizationGroup from './components/diarization-group.component';
import BotRunnerGroup from './components/bot-runner-group.component';
import { LLM_PROVIDER_PRESETS, localOnlyPresets } from './utils/ai-provider-presets';

const cn = createCn('ai-settings', classes);
const cn2 = createCn('files', classes2);

const MASKED = '***set***';

const EMBEDDING_PRESETS = localOnlyPresets('http://tei:80/v1');
const VLM_PRESETS = localOnlyPresets('http://vllm:8000/v1');

/** kind -> поля формы, которые проверка подключения отправляет как оверрайды. */
const TEST_FIELDS: Record<AiProviderKind, { baseUrl: keyof AiSettings; apiKey: keyof AiSettings; model: keyof AiSettings }> = {
  inference: { baseUrl: 'inference_base_url', apiKey: 'inference_api_key', model: 'llm_model' },
  embedding: { baseUrl: 'embedding_base_url', apiKey: 'embedding_api_key', model: 'embedding_model' },
  vlm: { baseUrl: 'vlm_base_url', apiKey: 'vlm_api_key', model: 'vlm_model' },
};

const EMPTY_SETTINGS: AiSettings = {
  inference_base_url: '',
  inference_api_key: '',
  llm_model: '',
  embedding_base_url: '',
  embedding_api_key: '',
  embedding_model: '',
  vlm_base_url: '',
  vlm_api_key: '',
  vlm_model: '',
  hf_token: '',
  bot_runner_enabled: 'false',
  bot_runner_url: '',
  bot_runner_api_key: '',
};

const AiSettingsPage = () => {
  const [form, setForm] = useState<AiSettings>(EMPTY_SETTINGS);
  const [keySet, setKeySet] = useState({
    inference: false,
    embedding: false,
    vlm: false,
    hfToken: false,
    botRunnerApiKey: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<Record<AiProviderKind, boolean>>({
    inference: false,
    embedding: false,
    vlm: false,
  });
  const [testResult, setTestResult] = useState<Record<AiProviderKind, AiTestResult | null>>({
    inference: null,
    embedding: null,
    vlm: null,
  });
  /** Живой список моделей провайдера per-group (задача M1): сбрасывается при смене base_url/пресета. */
  const [models, setModels] = useState<Record<AiProviderKind, string[]>>({
    inference: [],
    embedding: [],
    vlm: [],
  });
  const [modelsLoading, setModelsLoading] = useState<Record<AiProviderKind, boolean>>({
    inference: false,
    embedding: false,
    vlm: false,
  });
  const [modelsError, setModelsError] = useState<Record<AiProviderKind, string>>({
    inference: '',
    embedding: '',
    vlm: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await aiSettingsService.getAiSettings();
      setForm({
        ...data,
        inference_api_key: '',
        embedding_api_key: '',
        vlm_api_key: '',
        hf_token: '',
        bot_runner_api_key: '',
      });
      setKeySet({
        inference: data.inference_api_key === MASKED,
        embedding: data.embedding_api_key === MASKED,
        vlm: data.vlm_api_key === MASKED,
        hfToken: data.hf_token === MASKED,
        botRunnerApiKey: data.bot_runner_api_key === MASKED,
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Не удалось загрузить настройки AI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key: keyof AiSettings, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /** Сброс живого списка моделей группы (смена base_url/пресета делает старый список неактуальным). */
  const resetModels = (kind: AiProviderKind) => {
    setModels((prev) => ({ ...prev, [kind]: [] }));
    setModelsError((prev) => ({ ...prev, [kind]: '' }));
  };

  const setBaseUrl = (kind: AiProviderKind, key: keyof AiSettings, value: string) => {
    setField(key, value);
    resetModels(kind);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiSettingsService.updateAiSettings(form);
      toast.success('Настройки AI сохранены');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось сохранить настройки AI');
    } finally {
      setSaving(false);
    }
  };

  const handleFetchModels = useCallback(
    async (kind: AiProviderKind) => {
      const fields = TEST_FIELDS[kind];
      setModelsLoading((prev) => ({ ...prev, [kind]: true }));
      setModelsError((prev) => ({ ...prev, [kind]: '' }));
      try {
        const result = await aiSettingsService.fetchProviderModels({
          kind,
          base_url: form[fields.baseUrl] || undefined,
          api_key: form[fields.apiKey] || undefined,
        });
        if (result.ok && result.models && result.models.length > 0) {
          setModels((prev) => ({ ...prev, [kind]: result.models! }));
        } else {
          const message = result.error || 'Провайдер не отдаёт список моделей — введите имя вручную';
          setModels((prev) => ({ ...prev, [kind]: [] }));
          setModelsError((prev) => ({ ...prev, [kind]: message }));
          toast.error(message);
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Не удалось загрузить список моделей';
        setModels((prev) => ({ ...prev, [kind]: [] }));
        setModelsError((prev) => ({ ...prev, [kind]: message }));
        toast.error(message);
      } finally {
        setModelsLoading((prev) => ({ ...prev, [kind]: false }));
      }
    },
    [form],
  );

  const handleTest = async (kind: AiProviderKind) => {
    setTesting((prev) => ({ ...prev, [kind]: true }));
    setTestResult((prev) => ({ ...prev, [kind]: null }));
    const fields = TEST_FIELDS[kind];
    try {
      const result = await aiSettingsService.testAiConnection({
        kind,
        base_url: form[fields.baseUrl] || undefined,
        // Пустой api_key в форме = «использовать сохранённый» (как при сохранении).
        api_key: form[fields.apiKey] || undefined,
        model: form[fields.model] || undefined,
      });
      setTestResult((prev) => ({ ...prev, [kind]: result }));
      if (result.ok) {
        // Автозагрузка списка моделей сразу после успешной проверки подключения.
        void handleFetchModels(kind);
      }
    } catch (e) {
      setTestResult((prev) => ({
        ...prev,
        [kind]: { ok: false, error: e instanceof Error ? e.message : 'Ошибка проверки подключения' },
      }));
    } finally {
      setTesting((prev) => ({ ...prev, [kind]: false }));
    }
  };

  return (
    <div className={cn2('')}>
      <div className={cn2('heading')}>Интеграции</div>

      <div className={cn2('content')}>
        <p className={cn('subtitle')}>
          Подключите AI-провайдеров, диаризацию и бот-раннера для встреч. Изменения применяются на
          сервере сразу после сохранения.
        </p>

        {loading ? (
          <div className={cn('status')}>Загрузка…</div>
        ) : (
          <>
            {loadError && <div className={cn('error-banner')}>{loadError}</div>}

            <div className={cn('groups')}>
              <div className={cn('section-title')}>AI-провайдеры</div>

              <AiSettingsGroup
                title="LLM (инференс)"
                description="Модель, которая генерирует протоколы, конспекты и отвечает в AI-чате."
                baseUrl={form.inference_base_url}
                onBaseUrlChange={(v) => setBaseUrl('inference', 'inference_base_url', v)}
                apiKeySet={keySet.inference}
                apiKeyValue={form.inference_api_key}
                onApiKeyChange={(v) => setField('inference_api_key', v)}
                model={form.llm_model}
                onModelChange={(v) => setField('llm_model', v)}
                presets={LLM_PROVIDER_PRESETS}
                onPresetChange={() => resetModels('inference')}
                onTest={() => handleTest('inference')}
                testing={testing.inference}
                testResult={testResult.inference}
                models={models.inference}
                modelsLoading={modelsLoading.inference}
                modelsError={modelsError.inference}
                onFetchModels={() => handleFetchModels('inference')}
              />

              <AiSettingsGroup
                title="Эмбеддинги"
                description="Модель для векторного поиска и идентификации спикеров (Qdrant)."
                baseUrl={form.embedding_base_url}
                onBaseUrlChange={(v) => setBaseUrl('embedding', 'embedding_base_url', v)}
                apiKeySet={keySet.embedding}
                apiKeyValue={form.embedding_api_key}
                onApiKeyChange={(v) => setField('embedding_api_key', v)}
                model={form.embedding_model}
                onModelChange={(v) => setField('embedding_model', v)}
                presets={EMBEDDING_PRESETS}
                onPresetChange={() => resetModels('embedding')}
                onTest={() => handleTest('embedding')}
                testing={testing.embedding}
                testResult={testResult.embedding}
                models={models.embedding}
                modelsLoading={modelsLoading.embedding}
                modelsError={modelsError.embedding}
                onFetchModels={() => handleFetchModels('embedding')}
              />

              <AiSettingsGroup
                title="VLM (анализ видео)"
                description="Модель для распознавания слайдов и экрана в видеозаписях."
                baseUrl={form.vlm_base_url}
                onBaseUrlChange={(v) => setBaseUrl('vlm', 'vlm_base_url', v)}
                apiKeySet={keySet.vlm}
                apiKeyValue={form.vlm_api_key}
                onApiKeyChange={(v) => setField('vlm_api_key', v)}
                model={form.vlm_model}
                onModelChange={(v) => setField('vlm_model', v)}
                presets={VLM_PRESETS}
                onPresetChange={() => resetModels('vlm')}
                onTest={() => handleTest('vlm')}
                testing={testing.vlm}
                testResult={testResult.vlm}
                models={models.vlm}
                modelsLoading={modelsLoading.vlm}
                modelsError={modelsError.vlm}
                onFetchModels={() => handleFetchModels('vlm')}
              />

              <div className={cn('section-title')}>Диаризация</div>

              <DiarizationGroup
                tokenSet={keySet.hfToken}
                tokenValue={form.hf_token}
                onTokenChange={(v) => setField('hf_token', v)}
              />

              <div className={cn('section-title')}>Бот-раннер</div>

              <BotRunnerGroup
                enabled={form.bot_runner_enabled === 'true'}
                onEnabledChange={(v) => setField('bot_runner_enabled', v ? 'true' : 'false')}
                url={form.bot_runner_url}
                onUrlChange={(v) => setField('bot_runner_url', v)}
                apiKeySet={keySet.botRunnerApiKey}
                apiKeyValue={form.bot_runner_api_key}
                onApiKeyChange={(v) => setField('bot_runner_api_key', v)}
              />
            </div>

            <button type="button" className={cn('save-btn')} onClick={handleSave} disabled={saving}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AiSettingsPage;
